import axios from 'axios';
import { useAuthStore } from '../../store';

/**
 * API_URL resolution priority:
 *   1. BACKEND_URL override (runtime config or build-time env), if non-empty.
 *      Used ONLY for split-host development setups. Logs a visible console
 *      warning so a stale override is never silent.
 *   2. Same-origin (window.location.origin). This is the production default:
 *      the custom domain proxies /api to the backend, so no absolute host is
 *      needed.
 *
 * An empty-string or whitespace-only BACKEND_URL is treated as absent (not
 * as a real value), preventing "undefined/api/..." or "/api/..." breakage.
 */
const _backendOverride = (function resolveBackendOverride() {
  const rt =
    typeof window !== 'undefined' &&
    window.__RUNTIME_CONFIG__ &&
    window.__RUNTIME_CONFIG__.BACKEND_URL;
  if (rt && typeof rt === 'string' && rt.trim()) return rt.trim();
  if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.trim())
    return process.env.REACT_APP_BACKEND_URL.trim();
  return '';
})();

const API_URL = _backendOverride || (typeof window !== 'undefined' && window.location && window.location.origin) || '';

if (!API_URL) {
  throw new Error(
    'Cannot determine API target: window.location.origin is unavailable ' +
    'and no BACKEND_URL override is configured.'
  );
}

if (_backendOverride) {
  // eslint-disable-next-line no-console
  console.warn(
    '[api] BACKEND_URL override is active: %s. ' +
    'API calls will target this host instead of same-origin (%s). ' +
    'This is expected only in development.',
    _backendOverride,
    (typeof window !== 'undefined' && window.location && window.location.origin) || '(unknown)',
  );
}

/**
 * Derive the origin sent as the OAuth callback base.
 * Priority: window.location.origin (the stable custom domain in production).
 * Falls back to BACKEND_URL override only when location is unavailable
 * (e.g. SSR or test harness).
 */
function getOAuthCallbackOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  if (_backendOverride) {
    try {
      return new URL(_backendOverride).origin;
    } catch (_) {
      /* malformed override -- fall through to error */
    }
  }
  throw new Error(
    'Cannot determine OAuth callback origin: window.location.origin is ' +
    'unavailable and no valid BACKEND_URL override is configured.'
  );
}

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000, // 30 second timeout for all requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors with automatic token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is an auth flow request - don't retry these
      if (originalRequest.url?.includes('/auth/google/') ||
          originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const refreshToken = useAuthStore.getState().refreshToken;
      
      if (!refreshToken) {
        isRefreshing = false;
        useAuthStore.getState().logout();
        window.location.replace('/login');
        return Promise.reject(error);
      }
      
      try {
        // Call refresh endpoint
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken
        });
        
        // Update tokens in store
        useAuthStore.getState().updateToken(data.access_token, data.refresh_token);
        
        // Process queued requests
        processQueue(null, data.access_token);
        
        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return api(originalRequest);
        
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthStore.getState().logout();
        window.location.replace('/login');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // For other errors (429 rate limit, etc.), just reject
    return Promise.reject(error);
  }
);

export { API_URL };
export { getOAuthCallbackOrigin };
export default api;
